import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DateTime } from 'luxon';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import Link from '@material-ui/core/Link';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBackIos';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

import SearchUsersAutocomplete from 'app/components/user/UserSearchAutocomplete';
import GreenButton from 'app/components/buttons/GreenButton';
import RedButton from 'app/components/buttons/RedButton';
import Loading from 'app/components/loaders/Loading';
import Field from 'app/components/form/Field';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import DefaultFieldValue from 'app/utils/DefaultFieldValue';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogActions: {
        justifyContent: 'space-between'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(1)
    },
    textCenter: {
        textAlign: "center"
    },
    textRight: {
        textAlign: "right"
    }
}))

export default function EditProposalDialog(props) {

    // Construct -----------------------------

    const {
        election,
        id,
        open,
        onClose,
        onSave,
        onDelete,
        onClosed,
        admin,
        copy,
    } = props;

    const urlPrefix = (admin ? '/admin/' : '/') + `election/${election.id}/proposal`;
    const spp = election?.sponsors_per_proposal + admin ? 1 : 0;

    const api = useApi();
    const { app } = useApp();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ proposal, setProposal ] = useState({status: 'active', cycles_repeated: 1});
    const [ values, setValues ] = useState({});
    const [ recommendations, setRecommendations ] = useState([]);
    const [ users, setUsers ] = useState([]);
    const [ primaryUser, setPrimaryUser ] = useState({id: 0});
    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ step, setStep ] = useState(1);

    const getProposal = () => {
        setLoading(true);

        api.get(`${urlPrefix}/${id}` + (copy ? `/copy` : ''))
            .then(res => {
                mapValues(election.fields, res.proposal.values);
                setRecommendations(res.proposal.recommendations);
                setProposal(res.proposal);

                if (res.proposal.users && res.proposal.users.length > 0) {
                    setUsers(res.proposal.users);
                    setPrimaryUser(res.proposal.users.find(user => user.pivot.primary == true));
                } else if (! admin) {
                    setUsers([app.user]);
                    setPrimaryUser(app.user);
                }
            })
            .finally(() => setLoading(false));
    }

    const mapValues = (fields, values) => {

        const newValues = {};

        // Map the values to their fields
        fields.forEach(f => {
            
            const v = values ? values.find(val => {
                if (copy) return (val.field_id === f.options['former_id'])
                return (val.field_id === f.id)
        }) : false;

            if (v) {
                
                newValues[f.id] = v.value;
                
                if (f.options.type === 'userselect') {
                    newValues[f.id].users = v.users;
                }
                if (f.options.type === 'upload') {
                    newValues[f.id].file = v.file;
                }
            } else {
                const dv = DefaultFieldValue(f);
                if (dv) newValues[f.id] = dv;
            }
        });

        setValues(newValues);
    }

    useEffect(() => {
        mapValues(election.fields, null);
    }, []);

    const onOpen = () => {

        if (id > 0) {
            getProposal();
        } else {
            mapValues(election.fields, null);
        }     

        setErrors({});
        setStep(1);
    };

    // Handlers -------------------------------------------------

    const handleChange = (index , event) => {

        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        };

        if (errors?.values?.[index]) delete errors.values[index];

        setValues({ ...values, [index]: { value: value} });

    };

    const increaseCyclesRepeated = () => {
        let cr = proposal.cycles_repeated + 1;
        setProposal({...proposal, cycles_repeated: cr});
    }

    const decreaseCyclesRepeated = () => {
        let cr = proposal.cycles_repeated - 1;

        if (cr >= 0) setProposal({...proposal, cycles_repeated: cr});
    }

    const addUser = user => {

        if ((user.id === app.user.id) && !admin) {
            setErrors({
                users: 'You are already considered a sponsor for this proposal. Please choose someone other than yourself.'
            });
            return false;
        }

        if (users.length >= spp) {
            setErrors({ users: `Limited to ${spp} sponsors.`});
            return false;
        }

        const newUsers = [...users];

        newUsers.push(user);

        if (isError('users')) delete errors.users;

        setUsers(newUsers);

        if (admin && primaryUser.id === 0) setPrimaryUser(user);
    }

    const removeUser = id => {
        const newUsers = [...users];

        const i = newUsers.findIndex(user => user.id === id)

        newUsers.splice(i,1);

        if (isError('users')) delete errors.users;

        setUsers(newUsers);

        if (admin && (id === primaryUser.id)) {
            if (newUsers.length) {
                setPrimaryUser(newUsers[0]);
            } else {
                setPrimaryUser({ id: 0});
            }
        }
    }

    const handlePrimaryUser = user => {
        if (admin) setPrimaryUser(user);
    }

    // Posters ---------------------------------

    const postProposal = () => {

        setLoading(true);

        const url = admin ? '/admin' : '';

        const data = {
            values: values,
            users: users.map(user => user.id),
            primary_user_id: primaryUser.id,
            copy: copy ? id : false,
            cycles_repeated: proposal.cycles_repeated
        }

        api.post(`${url}/election/${election.id}/proposal/${copy ? 0 : id}`, data)
            .then(() => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                }
            }).finally(() => setLoading(false))
    }

    const postAcceptProposal = () => {

        setLoading(true);

        api.get(`/admin/election/${election.id}/proposal/${id}/accept`)
            .then(() => onSave())
            .finally(() => setLoading(false))
    }

    const postRejectProposal = () => {

        setLoading(true);

        api.get(`/admin/election/${election.id}/proposal/${id}/reject`)
            .then(() => onSave())
            .finally(() => setLoading(false))
    }

    const postActiveProposal = () => {

        setLoading(true);

        api.get(`/admin/election/${election.id}/proposal/${id}/active`)
            .then(() => onSave())
            .finally(() => setLoading(false))
    }

    const deleteProposal = () => {

        setLoading(true);

        const url = admin ? '/admin' : '';

        api.delete(`${url}/election/proposal/${id}`)
            .then(() => onDelete())
            .finally(() => setLoading(false))
    }

    // Error Handling

    const isError = field => {
        if (errors[field]) return true;
        return false;
    };

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    };

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    const onExited = () => {
        setValues([]);
        setUsers([]);
        setPrimaryUser({id: 0});
        setErrors({});
        setLoading(false);
        setStep(1);
        if (onClosed) onClosed();
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            onEnter={onOpen}
            onExited={onExited}
            maxWidth="sm"
            aria-labelledby="edit-proposal-dialog-title"
            disableBackdropClick
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-proposal-dialog-title">
                {(step === 1) ? ((id > 0 && !copy) ? 'Edit Proposal' : "New Proposal") : 'Delete Proposal'}
                <IconButton
                    aria-label="Cancel"
                    className={classes.closeButton}
                    onClick={onClose}
                    disabled={loading}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {(step === 1) &&
                    <>
                        <Grid container spacing={3} className={classes.marginBottom}>
                            {election.fields.map(field => (
                                <Field
                                    key={field.id}
                                    field={field}
                                    fields={election.fields}
                                    value={values[field.id]}
                                    values={values}
                                    handleChange={handleChange}
                                    error={isError(field.id)}
                                    errorMessage={getErrorMessage(field.id)}
                                    admin={admin}
                                />
                            ))}
                        </Grid>
                        {spp > 0 &&
                            <Grid container spacing={3} className={classes.marginBottom}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Sponsors
                                    </Typography>
                                    <Typography variant="body1">
                                        Select up to <Typography component="span" color="primary"><strong>{spp}</strong></Typography> Sponsor(s) for this Proposal. Users will be notified of their sponsorship and will have full access to the proposal.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    { (users && users.length > 0) && users.map(user => (
                                        <CardHeader
                                            key={user.id}
                                            avatar={
                                                <Avatar
                                                    aria-label={user.full_name}
                                                    alt={user.full_name}
                                                    className={classes.avatar}
                                                    src={user.avatar ? user.avatar.full_url : null}
                                                    >
                                                    {initials(user)}
                                                </Avatar>
                                            }
                                            title={user.full_name}
                                            subheader={
                                                (primaryUser.id === user.id) ? (
                                                    <Typography color="secondary" variant="subtitle2">
                                                        Primary Sponsor
                                                    </Typography>
                                                ) : admin ? (
                                                    <Link color="primary" onClick={() => handlePrimaryUser(user)} href="#">
                                                        Make Primary
                                                    </Link>
                                                ) : null
                                            }
                                            action={
                                                <Tooltip
                                                    title={(primaryUser.id === user.id) ? 'The Primary Sponsor can not be removed.' : "Remove Sponsor"}
                                                    >
                                                    <span>
                                                        <IconButton
                                                            aria-label="Remove User"
                                                            onClick={() => removeUser(user.id)}
                                                            disabled={((primaryUser.id === user.id) && !admin)}
                                                            >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            }
                                        />
                                    ))}
                                    <SearchUsersAutocomplete
                                        closeOnSelect
                                        clearOnSelect
                                        onSelect={addUser}
                                        label='Add a Sponsor...'
                                        admin={admin}
                                    />
                                    { isError('users') &&
                                        <Typography variant="caption" color="error">
                                            {getErrorMessage('users')}
                                        </Typography>
                                    }
                                </Grid>
                            </Grid>
                        }
                        {(admin) && (
                            <Grid container spacing={3} className={classes.marginBottom} alignItems='center'>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Cycles Proposed
                                    </Typography>
                                    <Typography variant="body1">
                                        Adjust the number of cycles this proposal has been submitted.
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} className={classes.textRight}>
                                    <IconButton aria-label="decrease" color="secondary" onClick={decreaseCyclesRepeated}>
                                        <ArrowDownwardIcon fontSize="large" />
                                    </IconButton>
                                </Grid>
                                <Grid item xs={4} className={classes.textCenter}>
                                    <Typography variant="h4" color="secondary">
                                        {proposal.cycles_repeated}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <IconButton aria-label="increase" color="secondary" onClick={increaseCyclesRepeated}>
                                        <ArrowUpwardIcon fontSize="large" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        )}
                        {(id > 0 && admin) && (
                            <Grid container spacing={3} className={classes.marginBottom}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Recommendations
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    { (recommendations && recommendations.length > 0) ? recommendations.map(rec => (
                                        <CardHeader
                                            key={rec.id}
                                            avatar={
                                                <Avatar
                                                    aria-label={rec.user.full_name}
                                                    alt={rec.user.full_name}
                                                    className={classes.avatar}
                                                    src={rec.user.avatar ? rec.user.avatar.full_url : null}
                                                    >
                                                    {initials(rec.user)}
                                                </Avatar>
                                            }
                                            title={rec.content}
                                            subheader={` - ${rec.user.full_name}`}
                                        />
                                    )) : (
                                        <Typography variant="body1" color="textSecondary">
                                            This proposal does not have any recommendations at this time.
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        )}
                        <Typography variant="subtitle1">
                            Click <strong>{(id > 0 && !copy) ? 'Update Proposal' : 'Submit Proposal'}</strong> to save your proposal.
                        </Typography>
                    </>
                }
                {(step === 2) &&
                    <>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            All recommendations for your proposal will be deleted and any related users will be notified.
                        </Typography>
                        <Typography variant="subtitle1" color="error" gutterBottom>
                            Are you sure you want to delete this proposal?
                        </Typography>
                    </>
                }
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                {(step === 1) &&
                    <>
                        <Tooltip
                            title={((primaryUser.id !== app.user.id) && !admin) ? 'Must be the Primary Sponsor to delete.' : "Delete Proposal"}
                            >
                            <span>
                                <RedButton
                                    onClick={() => setStep(2)}
                                    disabled={loading || (app.user.id !== primaryUser.id && !admin) || id === 0 || copy}
                                    >
                                    Delete
                                </RedButton>
                            </span>
                        </Tooltip>
                        <div>
                            {(id > 0 && admin && proposal.status === 'active') && (
                                <>
                                    <RedButton
                                        variant="outlined"
                                        color="secondary"
                                        onClick={postRejectProposal}
                                        disabled={loading}
                                        className={classes.marginRight}
                                        >
                                        Reject
                                    </RedButton>
                                    <GreenButton
                                        variant="outlined"
                                        color="secondary"
                                        onClick={postAcceptProposal}
                                        disabled={loading}
                                        className={classes.marginRight}
                                        >
                                        Accept
                                    </GreenButton>
                                </>
                            )}
                            {(id > 0 && admin && (proposal.status === 'rejected' || proposal.status === 'accepted')) && (
                                <>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={postActiveProposal}
                                        disabled={loading}
                                        className={classes.marginRight}
                                        >
                                        Make Active
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={postProposal}
                                disabled={loading}
                                >
                                {(id > 0 && !copy) ? 'Update' : 'Submit Proposal'}
                            </Button>
                        </div>
                    </>
                }
                {(step === 2) &&
                    <>
                        <Button
                            startIcon={<LeftIcon/>}
                            aria-label="Back"
                            onClick={() => setStep(1)}
                            >
                            Back to Proposal
                        </Button>
                        <RedButton
                            onClick={deleteProposal}
                            disabled={loading}
                            variant="contained"
                            >
                            Delete
                        </RedButton>
                    </>
                }
            </DialogActions>
        </Dialog>
    )
}
